import { useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../../../../utils/axiosInstance";
import { AuthContext } from "../../../../context/AuthContext";
import { MODULES, isReadOnly } from "../../../../utils/constants/permissions";
import ConfirmModal from "../../../../components/Modals/ConfirmModal";
import SystemValueList from "../../../../components/SystemValue/SystemValueList";
import { toast } from "../../../../components/Toast/toast";
import { btn } from "../../../../components/Button/buttonStyles";
import PartyTaxForm from "./PartyTaxForm";
import { formatTaxValue, taxTypeLabel } from "./taxFormat";

const PARTIES = {
  client: {
    endpoint: "/client-taxes",
    module: MODULES.CLIENTS,
    documents: "quotes and invoices issued to this client",
    documentsShort: "quotes and invoices",
  },
  vendor: {
    endpoint: "/vendor-taxes",
    module: MODULES.VENDORS,
    documents: "job POs and vendor invoices for this vendor",
    documentsShort: "job POs and vendor invoices",
  },
};

// Absolute taxes are in the client's or vendor's default currency.
const columns = (ownerCurrency) => [
  { header: "Name", render: (t) => t.name || "—" },
  { header: "Type", render: (t) => taxTypeLabel(t.type) },
  { header: "Default Value", render: (t) => formatTaxValue(t) },
  {
    header: "Currency",
    render: (t) =>
      (t.type === "absolute" ? ownerCurrency?.code : null) || "—",
  },
];

const errorMessage = (err, fallback) =>
  err.response?.data?.error || err.response?.data?.message || fallback;

const byName = (a, b) => a.name.localeCompare(b.name);

const PartyTaxesPage = ({ party, ownerId }) => {
  const { user } = useContext(AuthContext);
  const { documents, documentsShort } = PARTIES[party];
  const readOnly = isReadOnly(user, PARTIES[party].module);
  const endpoint = `${PARTIES[party].endpoint}/${ownerId}`;

  const [customTaxes, setCustomTaxes] = useState(false);
  const [taxes, setTaxes] = useState([]);
  const [ownerCurrency, setOwnerCurrency] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [switching, setSwitching] = useState(false);
  const [confirmInherit, setConfirmInherit] = useState(false);
  const [taxToDelete, setTaxToDelete] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const action = readOnly ? null : searchParams.get("action");
  const editId = searchParams.get("id");

  const applySummary = (data) => {
    setCustomTaxes(data.custom_taxes);
    setTaxes(data.taxes);
    setOwnerCurrency(data.currency ?? null);
  };

  useEffect(() => {
    api
      .get(endpoint)
      .then((res) => applySummary(res.data.data))
      .catch((err) => setError(errorMessage(err, `Failed to load the ${party}'s taxes.`)))
      .finally(() => setLoading(false));
  }, [endpoint, party]);

  const setAction = (nextAction, id) => {
    const next = new URLSearchParams(searchParams);
    if (nextAction) next.set("action", nextAction);
    else next.delete("action");
    if (id !== undefined && id !== null) next.set("id", String(id));
    else next.delete("id");
    setSearchParams(next, { replace: true });
  };

  const switchMode = async (mode) => {
    setSwitching(true);
    try {
      const res = await api.post(`${endpoint}/${mode}`);
      applySummary(res.data.data);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(errorMessage(err, `Could not change the ${party}'s tax settings.`));
    } finally {
      setSwitching(false);
      setConfirmInherit(false);
    }
  };

  const handleSave = async (payload) => {
    try {
      if (action === "edit") {
        const res = await api.put(`${endpoint}/taxes/${editId}`, payload);
        setTaxes((prev) =>
          prev.map((t) => (String(t.id) === String(editId) ? res.data.data : t)).sort(byName)
        );
      } else {
        const res = await api.post(`${endpoint}/taxes`, payload);
        setTaxes((prev) => [...prev, res.data.data].sort(byName));
      }
      toast.success(action === "edit" ? "Tax updated." : "Tax added.");
      setAction(null);
      return null;
    } catch (err) {
      const message = errorMessage(err, "Could not save the tax.");
      if (err.response?.status === 409 && /name/i.test(message)) return { field: "name", message };
      return { message };
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`${endpoint}/taxes/${taxToDelete}`);
      setTaxes((prev) => prev.filter((t) => t.id !== taxToDelete));
      toast.success("Tax deleted.");
    } catch (err) {
      toast.error(errorMessage(err, "Failed to delete the tax."));
    } finally {
      setTaxToDelete(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <p className="text-gray-500 text-sm animate-pulse">Loading taxes...</p>
      </div>
    );
  }

  if (error) return <p className="text-center mt-10 text-danger-600 text-sm">{error}</p>;

  const editingTax =
    action === "edit" ? taxes.find((t) => String(t.id) === String(editId)) : null;

  if (customTaxes && (action === "add" || editingTax)) {
    return (
      <div className="md:w-2/3">
        <PartyTaxForm
          key={editingTax?.id ?? "new"}
          noun="Tax"
          taxToEdit={editingTax}
          currency={ownerCurrency}
          onSave={handleSave}
          onCancel={() => setAction(null)}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4 md:w-2/3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
          Default taxes set for {documentsShort}
        </h3>
        {!readOnly &&
          (customTaxes ? (
            <div className="flex gap-2">
              <button
                onClick={() => setConfirmInherit(true)}
                disabled={switching}
                className={btn("secondary", "sm")}
              >
                Inherit Account Settings
              </button>
              <button onClick={() => setAction("add")} className={btn("primary", "sm")}>
                Add Tax
              </button>
            </div>
          ) : (
            <button
              onClick={() => switchMode("override")}
              disabled={switching}
              className={btn("primary", "sm")}
            >
              {switching ? "Overriding..." : "Override Account Settings"}
            </button>
          ))}
      </div>

      <p className="rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm text-gray-600 shadow-sm">
        {customTaxes
          ? `Custom tax set defined below is applied upon creation of ${documents}.`
          : `Default tax set defined in account settings is applied upon creation of ${documents}.`}
      </p>

      {customTaxes && (
        <SystemValueList
          items={taxes}
          columns={columns(ownerCurrency)}
          onEdit={(tax) => setAction("edit", tax.id)}
          onDelete={(id) => setTaxToDelete(id)}
          showStatus={false}
          showActions={!readOnly}
          emptyMessage={`No taxes added for this ${party} yet.`}
        />
      )}

      {confirmInherit && (
        <ConfirmModal
          title="Inherit Account Settings"
          message={`This ${party}'s custom taxes will be removed and the default taxes from account settings will apply to its new ${documentsShort}.`}
          onCancel={() => setConfirmInherit(false)}
          onConfirm={() => switchMode("inherit")}
          confirmText="Inherit"
        />
      )}

      {taxToDelete && (
        <ConfirmModal
          title="Confirm Deletion"
          message={`Are you sure you want to delete this tax for this ${party}?`}
          onCancel={() => setTaxToDelete(null)}
          onConfirm={handleDelete}
          confirmText="Delete"
        />
      )}
    </div>
  );
};

export default PartyTaxesPage;